import React, { createContext, useContext } from 'react'
import type { HTMLAttributes } from 'react'
import {
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuItemTrailing,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from '@/src/renderer/components/ui/context-menu'
import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuItemTrailing,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/src/renderer/components/ui/dropdown-menu'

interface ItemProps {
  className?: string
  disabled?: boolean
  onSelect?: (event: Event) => void
  children?: React.ReactNode
}

/**
 * Die Radix-Bausteine, aus denen die Zeilen des **Aufgaben-Menüs** (`TaskMenuRows`) gebaut sind.
 * Radix trennt Rechtsklick- und Klick-Menü in zwei Familien, deren Teile sich nicht mischen lassen;
 * dieselben Zeilen laufen deshalb in beiden nur, wenn sie ihre Teile von hier holen. Das ⋯ der
 * **Listen-Werkzeug-Pille** (#291) ist ein Klick-Menü — dieselben Klassen, dieselbe Optik.
 */
export interface MenuKit {
  Item: React.FC<ItemProps>
  Sub: React.FC<{ open?: boolean; onOpenChange?: (open: boolean) => void; children?: React.ReactNode }>
  SubTrigger: React.FC<{ disabled?: boolean; children?: React.ReactNode }>
  SubContent: React.FC<{ className?: string; children?: React.ReactNode }>
  Group: React.FC<{ children?: React.ReactNode }>
  Separator: React.FC<{ className?: string }>
  Shortcut: React.FC<HTMLAttributes<HTMLSpanElement>>
  ItemTrailing: React.FC<HTMLAttributes<HTMLSpanElement>>
}

export const contextMenuKit: MenuKit = {
  Item: ContextMenuItem,
  Sub: ContextMenuSub,
  SubTrigger: ContextMenuSubTrigger,
  SubContent: ContextMenuSubContent,
  Group: ContextMenuGroup,
  Separator: ContextMenuSeparator,
  Shortcut: ContextMenuShortcut,
  ItemTrailing: ContextMenuItemTrailing,
}

export const dropdownMenuKit: MenuKit = {
  Item: DropdownMenuItem,
  Sub: DropdownMenuSub,
  // Den Pfeil setzt `MenuSub` selbst, hinter Haken und Kürzel.
  SubTrigger: (props) => <DropdownMenuSubTrigger chevron={false} {...props} />,
  SubContent: DropdownMenuSubContent,
  Group: DropdownMenuGroup,
  Separator: DropdownMenuSeparator,
  Shortcut: DropdownMenuShortcut,
  ItemTrailing: DropdownMenuItemTrailing,
}

/** Ohne Provider: das Rechtsklick-Menü, wie bisher. */
export const MenuKitContext = createContext<MenuKit>(contextMenuKit)

export const useMenuKit = (): MenuKit => useContext(MenuKitContext)
